import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Phone, Search, Home, Bookmark, AlertCircle, Trash2, Square, CheckSquare } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { formatEuro, contractProgress, daysSince } from "@/lib/format";
import QuickCallDialog from "@/components/QuickCallDialog";
import LiveStatusBadge from "@/components/LiveStatusBadge";
import StatusChip from "@/components/StatusChip";
import { formatTimeSince } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";
import LeadBulkBar, { leadDeleteMessage } from "@/components/LeadBulkBar";
import BulkArchiveDialog from "@/components/BulkArchiveDialog";
import LeadFilterChips, { readFilterFromParams, writeFilterToParams, filterToApiParams } from "@/components/LeadFilterChips";
import { useLeadSelection } from "@/lib/useLeadSelection";

/**
 * Generic status view (both source_types). Used for:
 *   /zoeken     — leads with any status that targets parkeren_zoeken
 *   /shortlist  — parkeren_shortlist
 *   /getekend   — parkeren_signed
 *   /verloren   — parkeren_lost
 *
 * Title comes from the route (no "Parkeren ·" prefix).
 */
export default function StatusView({ targetView, title, sub }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const sourceFilter = params.get("source") || "all"; // all | makelaar | particulier
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLead, setCallLead] = useState(null);
  const [query, setQuery] = useState("");
  const chipFilter = readFilterFromParams(params);
  const setChipFilter = (next) => setParams(writeFilterToParams(next, params));

  // Request-token tegen race conditions tussen achtereenvolgende fetches.
  const reqIdRef = useRef(0);

  const refresh = async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    const p = { target_view: targetView, ...filterToApiParams(chipFilter) };
    if (sourceFilter !== "all") p.source_type = sourceFilter;
    try {
      const r = await http.get("/api/leads", { params: p });
      if (myId !== reqIdRef.current) return;
      setLeads(r.data);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  };
  useEffect(() => { setLeads([]); refresh(); /* eslint-disable-next-line */ },
    [targetView, sourceFilter, params.get("f_status"), params.get("f_live"), params.get("f_option"), params.get("f_pmin"), params.get("f_pmax")]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter(l =>
      l.address?.toLowerCase().includes(q) ||
      l.postal_code?.includes(q) ||
      l.city?.toLowerCase().includes(q) ||
      l.agent_name?.toLowerCase().includes(q) ||
      l.owner_name?.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const openLead = (id) => {
    navigate(`/lead/${id}`, {
      state: { from: location.pathname + location.search, list: filtered.map(l => l.id) },
    });
  };

  const sel = useLeadSelection(refresh);

  const counts = useMemo(() => ({
    all: leads.length,
    makelaar: leads.filter(l => l.source_type === "makelaar").length,
    particulier: leads.filter(l => l.source_type === "particulier").length,
  }), [leads]);

  return (
    <div className="flex-1 min-w-0 p-8 overflow-auto" data-testid="status-view-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            Pipeline
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            {title}
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1 max-w-2xl">
            {sub}
          </p>
        </div>
      </div>

      {/* Source filter chips */}
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="source-filter">
        <div className="flex items-center gap-1 bg-[var(--sand-100)] border border-[var(--border-soft)] rounded-lg p-1">
          {[
            { k: "all",         label: `Alle (${counts.all})` },
            { k: "makelaar",    label: `Makelaars (${counts.makelaar})` },
            { k: "particulier", label: `Particulieren (${counts.particulier})` },
          ].map(c => {
            const active = sourceFilter === c.k;
            return (
              <button key={c.k} type="button"
                      data-testid={`source-${c.k}`}
                      onClick={() => {
                        const n = new URLSearchParams(params);
                        if (c.k === "all") n.delete("source"); else n.set("source", c.k);
                        navigate(`${location.pathname}?${n.toString()}`);
                      }}
                      className={`px-3 h-8 rounded-md text-[12px] font-medium transition
                        ${active ? "bg-white text-[var(--teal-700)] shadow-sm"
                                 : "text-[var(--ink-500)] hover:text-[var(--ink-900)]"}`}>
                {c.label}
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
          <input
            data-testid="status-view-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`${t("common.search")}…`}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm focus:border-[var(--teal-500)] outline-none"
          />
        </div>
      </div>

      <div className="mb-5">
        <LeadFilterChips value={chipFilter} onChange={setChipFilter}
                         sourceType={sourceFilter} />
      </div>

      {loading ? (
        <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12 px-6" data-testid="status-view-empty">
          <h3 className="font-display text-[20px]">Geen leads in {title.toLowerCase()}</h3>
          <p className="text-[14px] text-[var(--ink-500)] mt-2 max-w-md mx-auto">
            Wanneer een lead deze status krijgt, verschijnt hij hier automatisch.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="status-view-list">
          {filtered.map(lead => (
            <Row key={lead.id}
                 lead={lead}
                 selected={sel.selected.has(lead.id)}
                 onToggle={() => sel.toggleOne(lead.id)}
                 onSelect={() => openLead(lead.id)}
                 onCall={() => setCallLead(lead)}
                 onDelete={() => sel.requestDelete([lead.id])} />
          ))}
        </ul>
      )}

      <LeadBulkBar
        count={sel.selected.size}
        total={filtered.length}
        allSelected={filtered.length > 0 && filtered.every(l => sel.selected.has(l.id))}
        onToggleAll={() => sel.toggleAll(filtered.map(l => l.id))}
        onClear={sel.clear}
        onArchive={() => sel.requestArchive(Array.from(sel.selected))}
        onDelete={() => sel.requestDelete(Array.from(sel.selected))}
      />

      <BulkArchiveDialog sel={sel} />

      <ConfirmDialog
        open={sel.confirmIds !== null}
        danger
        title={(sel.confirmIds?.length || 0) > 1 ? "Leads verwijderen" : "Lead verwijderen"}
        message={leadDeleteMessage(sel.confirmIds)}
        confirmLabel="Verwijderen"
        onConfirm={sel.doDelete}
        onCancel={sel.cancelDelete}
      />

      {callLead && (
        <QuickCallDialog lead={callLead} onClose={() => setCallLead(null)}
                         onSaved={() => { setCallLead(null); refresh(); }} />
      )}
    </div>
  );
}

function Row({ lead, onSelect, onCall, onDelete, selected, onToggle }) {
  const prog = contractProgress(lead.date_posted, lead.source_type);
  const days = daysSince(lead.date_posted);
  const isReady = (days || 0) >= 90;
  const hasPriceDrop = lead.old_price > 0 && lead.price > 0 && lead.old_price > lead.price;
  const thumb = lead.photos?.[0];
  const contactName = lead.source_type === "makelaar" ? lead.agent_name : lead.owner_name;
  return (
    <li onClick={onSelect}
        data-testid={`lead-row-${lead.id}`}
        className={`card cursor-pointer !p-4 grid grid-cols-12 gap-4 items-center transition-all
          ${selected
            ? "!border-[var(--teal-500)] !bg-[var(--sand-50)]"
            : isReady && lead.source_type === "makelaar"
              ? "!bg-[var(--teal-50)] !border-[var(--teal-500)] hover:!bg-[#D8EFEC]"
              : "hover:border-[var(--border-strong)]"}`}>
      <div className="col-span-1 shrink-0 flex items-center gap-2">
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
                data-testid={`lead-select-${lead.id}`}
                className="grid place-items-center w-6 h-6 text-[var(--ink-400)] hover:text-[var(--teal-700)]"
                title={selected ? "Deselecteren" : "Selecteren"}>
          {selected ? <CheckSquare size={18} className="text-[var(--teal-700)]" /> : <Square size={18} />}
        </button>
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--sand-200)] grid place-items-center">
          {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                 : <Home size={16} className="text-[var(--ink-400)]" />}
        </div>
      </div>
      <div className="col-span-4 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <StatusChip lead={lead} />
          <span className={`chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                            ${lead.source_type === "makelaar"
                              ? "bg-[var(--ink-100,#E5E7EB)] text-[var(--ink-700)] border-transparent"
                              : "bg-[var(--teal-100)] text-[var(--teal-800)] border-transparent"}`}>
            {lead.source_type === "makelaar" ? "Makelaar" : "Particulier"}
          </span>
          {isReady && lead.source_type === "makelaar" && (
            <span data-testid={`lead-ready-${lead.id}`}
                  className="chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                             bg-[var(--teal-700)] text-white border-transparent flex items-center gap-1">
              <AlertCircle size={11} /> ≥90d
            </span>
          )}
          {hasPriceDrop && (
            <span className="chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                             bg-[var(--warn,#f59e0b)] text-white border-transparent">
              prijsdaling
            </span>
          )}
          <LiveStatusBadge lead={lead} />
        </div>
        <div className="text-[15px] font-semibold text-[var(--ink-900)] truncate">
          {lead.address} · {lead.postal_code} {lead.city}
        </div>
        <div className="text-[12px] text-[var(--ink-500)] mt-1 flex flex-wrap gap-x-3">
          {contactName && <span>{contactName}</span>}
          <span>Online {formatTimeSince(lead.date_posted)}</span>
        </div>
      </div>
      <div className="col-span-2 tabular">
        <div className="text-[15px] font-semibold">{formatEuro(lead.price)}</div>
        {hasPriceDrop && (
          <div className="text-[12px] text-[var(--ink-400)] line-through">{formatEuro(lead.old_price)}</div>
        )}
      </div>
      <div className="col-span-3">
        <div className={`text-[13px] tabular font-semibold ${isReady ? "text-[var(--teal-700)]" : "text-[var(--ink-700)]"}`}>
          {formatTimeSince(lead.date_posted)} online
        </div>
        <div className="timebar mt-1.5">
          <div className={`timebar-fill ${prog.tone === "warn" ? "warn" : prog.tone === "danger" ? "danger" : ""}`}
               style={{ width: `${prog.pct}%` }} />
        </div>
      </div>
      <div className="col-span-2 flex justify-end items-center gap-2">
        {lead.shortlisted && <Bookmark size={16} className="text-[var(--teal-600)] fill-[var(--teal-500)]" />}
        <button type="button" onClick={(e) => { e.stopPropagation(); onCall(); }}
                data-testid={`btn-call-${lead.id}`}
                className="btn btn-ghost h-9 !px-3">
          <Phone size={14} /> {t("leads.call")}
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
                data-testid={`btn-delete-${lead.id}`}
                title="Lead verwijderen"
                className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]">
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}
